import React, { useState } from 'react';
import { Save, RotateCcw, CheckCircle, AlertTriangle, BookOpen, ChevronRight, Plus, X, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_BRAND_GUIDELINES, DEFAULT_CATEGORY_TEMPLATES } from '../lib/constants';
import { useData } from '../contexts/DataContext';

export default function TemplatesPage() {
  const data = useData();
  const [brandGuidelines, setBrandGuidelines] = useState(data.templates.brandGuidelines);
  const [categoryTemplates, setCategoryTemplates] = useState(data.templates.categoryTemplates);
  // Union: alla kategorier från context OCH alla kategorier som har ett template i DB.
  // Läker om app_settings.categories är stale mot templates-tabellen (post-V20 shared workspace).
  const initialCategories = () => {
    const base = data.categories.slice();
    Object.keys(data.templates.categoryTemplates || {}).forEach(c => {
      if (c && !base.includes(c)) base.push(c);
    });
    return base;
  };
  const [categories, setCategories] = useState(initialCategories);
  const [activeCategory, setActiveCategory] = useState(() => initialCategories()[0] || '');
  const [dirty, setDirty] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [copyFromCategory, setCopyFromCategory] = useState('');

  const handleSave = async () => {
    try {
      await data.saveTemplates(brandGuidelines, categoryTemplates);
      setDirty(false);
      toast.success('Mallar sparade — nästa generering använder uppdaterade riktlinjer');
    } catch (err) {
      const msg = err?.message || String(err);
      toast.error(`Kunde inte spara mallar: ${msg.slice(0, 200)}`);
      console.error('[TemplatesPage] save failed:', err);
    }
  };

  const handleReset = () => {
    if (!window.confirm('Återställa alla mallar till standardinställningarna? Detta tar bort anpassade kategorier.')) return;
    data.resetTemplates();
    setBrandGuidelines(DEFAULT_BRAND_GUIDELINES);
    setCategoryTemplates(DEFAULT_CATEGORY_TEMPLATES);
    const defaultCats = Object.keys(DEFAULT_CATEGORY_TEMPLATES);
    setCategories(defaultCats);
    setActiveCategory(defaultCats[0]);
    setDirty(false);
    toast.success('Mallar återställda till standard');
  };

  const updateCategoryTemplate = (cat, value) => {
    setCategoryTemplates((prev) => ({ ...prev, [cat]: value }));
    setDirty(true);
  };

  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) {
      toast.error('Ange ett kategorinamn');
      return;
    }
    if (categories.includes(name)) {
      toast.error('Kategorin finns redan');
      return;
    }
    const updated = [...categories, name];
    data.addCategory(name);
    setCategories(updated);
    setCategoryTemplates(prev => ({ ...prev, [name]: '' }));
    setActiveCategory(name);
    setNewCategoryName('');
    setShowAddModal(false);
    setDirty(true);
    toast.success(`Kategori "${name}" skapad — lägg till en mall för att aktivera`);
  };

  const handleCopyTemplate = () => {
    const name = newCategoryName.trim();
    if (!name) {
      toast.error('Ange ett kategorinamn');
      return;
    }
    if (categories.includes(name)) {
      toast.error('Kategorin finns redan');
      return;
    }
    if (!copyFromCategory) {
      toast.error('Välj en kategori att kopiera från');
      return;
    }
    const templateToCopy = categoryTemplates[copyFromCategory] || '';
    const updated2 = [...categories, name];
    data.addCategory(name);
    setCategories(updated2);
    setCategoryTemplates(prev => ({ ...prev, [name]: templateToCopy }));
    setActiveCategory(name);
    setNewCategoryName('');
    setCopyFromCategory('');
    setShowCopyModal(false);
    setDirty(true);
    toast.success(`Kategori "${name}" skapad med mall från "${copyFromCategory}"`);
  };

  const handleRemoveCategory = (cat) => {
    if (!window.confirm(`Ta bort kategorin "${cat}"? Mallen kommer att raderas.`)) return;
    const updated = categories.filter(c => c !== cat);
    data.removeCategory(cat);
    setCategories(updated);
    setCategoryTemplates(prev => {
      const next = { ...prev };
      delete next[cat];
      return next;
    });
    if (activeCategory === cat) {
      setActiveCategory(updated[0] || '');
    }
    toast.success(`Kategori "${cat}" borttagen`);
  };

  const isEnabled = (cat) => categoryTemplates[cat]?.trim().length > 0;
  const enabledCount = categories.filter(c => isEnabled(c)).length;
  const enabledCategories = categories.filter(c => isEnabled(c));

  return (
    <div className="page-scroll">
      <div className="page-header">
        <div>
          <h1 className="page-title">Mallar</h1>
          <p className="page-subtitle">
            Redigera varumärkesriktlinjer och kategorimallar. 
            <strong> {enabledCount} av {categories.length} kategorier aktiva.</strong>
          </p>
        </div>
        <div className="page-header-actions">
          {dirty && <span className="unsaved-badge"><AlertTriangle size={12} />Osparade ändringar</span>}
          <button className="btn-ghost" onClick={handleReset}>
            <RotateCcw size={14} />
            Återställ
          </button>
          <button 
            className={`generate-btn${dirty ? ' generate-btn--active' : ''} btn-sm`} 
            onClick={handleSave} 
            data-testid="save-templates-btn"
          >
            <Save size={14} />
            Spara mallar
          </button>
        </div>
      </div>

      <div className="templates-grid">
        {/* Brand Guidelines */}
        <div className="template-card">
          <div className="template-card-header">
            <div className="template-card-title-wrap">
              <BookOpen size={16} className="template-icon" />
              <div>
                <h2 className="template-card-title">Varumärkesriktlinjer</h2>
                <p className="template-card-desc">Ton, röst och stilriktlinjer för alla texter</p>
              </div>
            </div>
            <span className="template-badge template-badge--active">Aktiv</span>
          </div>
          <textarea
            data-testid="brand-guidelines-textarea"
            className="template-textarea"
            value={brandGuidelines}
            onChange={(e) => { setBrandGuidelines(e.target.value); setDirty(true); }}
            rows={22}
            spellCheck={false}
          />
        </div>

        {/* Category templates */}
        <div className="template-card">
          <div className="template-card-header">
            <div className="template-card-title-wrap">
              <div>
                <h2 className="template-card-title">Kategorimallar</h2>
                <p className="template-card-desc">Promptstruktur per produktkategori. En tom mall inaktiverar kategorin.</p>
              </div>
            </div>
            <div className="template-header-actions">
              {enabledCategories.length > 0 && (
                <button 
                  className="btn-copy-template"
                  onClick={() => { setCopyFromCategory(enabledCategories[0]); setShowCopyModal(true); }}
                  data-testid="copy-template-btn"
                >
                  <Copy size={14} />
                  Kopiera mall
                </button>
              )}
              <button 
                className="btn-add-category"
                onClick={() => setShowAddModal(true)}
                data-testid="add-category-btn"
              >
                <Plus size={14} />
                Ny kategori
              </button>
            </div>
          </div>

          {/* Category tabs */}
          <div className="cat-tabs-wrap">
            <div className="cat-tabs">
              {categories.map((cat) => (
                <div key={cat} className="cat-tab-container">
                  <button
                    data-testid={`cat-tab-${cat.toLowerCase().replace(/\s/g,'-')}`}
                    className={`cat-tab${activeCategory === cat ? ' cat-tab--active' : ''}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat}
                    {isEnabled(cat)
                      ? <span className="cat-dot cat-dot--on" />
                      : <span className="cat-dot cat-dot--off" />}
                  </button>
                  {categories.length > 1 && (
                    <button 
                      className="cat-tab-remove"
                      onClick={(e) => { e.stopPropagation(); handleRemoveCategory(cat); }}
                      title={`Ta bort ${cat}`}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {activeCategory && (
            <>
              <div className="cat-status">
                {isEnabled(activeCategory) ? (
                  <span className="cat-status-enabled">
                    <CheckCircle size={13} />
                    Aktiv — kategorin visas i Generera och Massimport
                  </span>
                ) : (
                  <span className="cat-status-disabled">
                    <AlertTriangle size={13} />
                    Inaktiv — lägg till en mall nedan för att aktivera {activeCategory}
                  </span>
                )}
              </div>

              <textarea
                data-testid={`category-template-textarea-${activeCategory}`}
                className="template-textarea template-textarea--cat"
                value={categoryTemplates[activeCategory] || ''}
                onChange={(e) => updateCategoryTemplate(activeCategory, e.target.value)}
                rows={22}
                spellCheck={false}
                placeholder={`Lägg till en promptmall för ${activeCategory} här för att aktivera kategorin.

Exempel på struktur:

## STEG 1: HOOK (30-40 ord)
Öppna med en konkret mening som sätter produkten i ett vårdsammanhang.

## STEG 2: FÖRDELAR (60-80 ord)
Beskriv 4-5 konkreta fördelar i naturligt flytande text.

## STEG 3: PRODUKTBESKRIVNING (80-100 ord)
Djupdyk i material, konstruktion och användning.

## STEG 4: SPECIFIKATIONER (30-40 ord)
Lista tekniska detaljer i ren text.

## STEG 5: AVSLUTNING (20-30 ord)
En varm avslutande rekommendation.

TOTALT: 200-400 ord. Plain text only.`}
              />

              <div className="template-tip">
                <ChevronRight size={12} />
                Tips: Kategorin aktiveras automatiskt när du lägger till en mall och trycker Spara
              </div>
            </>
          )}

          {!activeCategory && categories.length === 0 && (
            <div className="cat-empty-state">
              <p>Inga kategorier — klicka "Ny kategori" för att skapa din första.</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Ny kategori</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <label className="modal-label">Kategorinamn</label>
              <input
                type="text"
                className="modal-input"
                placeholder="T.ex. Stetoskop, Skor, Handskar..."
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                autoFocus
                data-testid="new-category-input"
              />
              <p className="modal-hint">
                Kategorin blir tillgänglig i Generera och Massimport när du lägger till en mall.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-ghost" onClick={() => setShowAddModal(false)}>
                Avbryt
              </button>
              <button 
                className="generate-btn generate-btn--active btn-sm"
                onClick={handleAddCategory}
                data-testid="confirm-add-category-btn"
              >
                <Plus size={14} />
                Skapa kategori
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Template Modal */}
      {showCopyModal && (
        <div className="modal-overlay" onClick={() => setShowCopyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Kopiera mall</h3>
              <button className="modal-close" onClick={() => setShowCopyModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <label className="modal-label">Kopiera från</label>
              <select
                className="modal-select"
                value={copyFromCategory}
                onChange={(e) => setCopyFromCategory(e.target.value)}
                data-testid="copy-from-select"
              >
                {enabledCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <p className="modal-hint modal-hint--small">
                Välj en befintlig kategori med mall att kopiera som startpunkt.
              </p>

              <label className="modal-label" style={{ marginTop: 16 }}>Ny kategorinamn</label>
              <input
                type="text"
                className="modal-input"
                placeholder="T.ex. Arbetskläder, Arbetsskor..."
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCopyTemplate()}
                data-testid="copy-new-category-input"
              />
              <p className="modal-hint">
                Den nya kategorin får en kopia av mallen som du kan anpassa.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-ghost" onClick={() => setShowCopyModal(false)}>
                Avbryt
              </button>
              <button 
                className="generate-btn generate-btn--active btn-sm"
                onClick={handleCopyTemplate}
                data-testid="confirm-copy-template-btn"
              >
                <Copy size={14} />
                Kopiera mall
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
